import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import SettingsModal from './SettingsModal';
import {
  formatDateForServer,
  isoToRuDisplay,
  parseBirthDateInput
} from '../utils/birthDateInput';
import './UserProfile.css';

const formatBirthRu = isoToRuDisplay;

const UserProfile = ({ user, onLogout, roleCheckStatus, isOpen, onClose, isMobileView, onUserUpdated }) => {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [birthDraft, setBirthDraft] = useState('');
  const [birthInputText, setBirthInputText] = useState('');
  const [editingBirth, setEditingBirth] = useState(false);
  const [birthSaving, setBirthSaving] = useState(false);
  const [birthError, setBirthError] = useState('');

  // Синхронизируем внешнее состояние с внутренним
  useEffect(() => {
    if (isOpen !== undefined) {
      setIsSettingsOpen(isOpen);
    }
  }, [isOpen]);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [telegramStatus, setTelegramStatus] = useState({ linked: false, username: null });
  const [telegramNotificationsStatus, setTelegramNotificationsStatus] = useState({ linked: false, username: null });
  const [telegramLinkUrl, setTelegramLinkUrl] = useState('');
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [hasPin, setHasPin] = useState(false);
  const [pinLoading, setPinLoading] = useState(false);
  const [pinError, setPinError] = useState('');
  const [pinSuccess, setPinSuccess] = useState('');
  const [showSetPin, setShowSetPin] = useState(false);
  const [showRemovePin, setShowRemovePin] = useState(false);
  const [setPinForm, setSetPinForm] = useState({ password: '', pin: '', confirm: '' });
  const [removePinPassword, setRemovePinPassword] = useState('');
  const { theme, changeTheme } = useTheme();

  useEffect(() => {
    // Получаем аватар Discord
    const fetchAvatar = async () => {
      if (!user?.discordUserId) return;

      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`/api/auth/discord-avatar`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setAvatarUrl(data.avatarUrl);
        } else {
          // Если не удалось получить аватар, используем дефолтный
          const discriminator = user.discordDiscriminator || '0';
          const defaultAvatarIndex = parseInt(discriminator) % 5;
          setAvatarUrl(`https://cdn.discordapp.com/embed/avatars/${defaultAvatarIndex}.png`);
        }
      } catch (error) {
        console.error('Error fetching avatar:', error);
        // Используем дефолтный аватар
        const discriminator = user.discordDiscriminator || '0';
        const defaultAvatarIndex = parseInt(discriminator) % 5;
        setAvatarUrl(`https://cdn.discordapp.com/embed/avatars/${defaultAvatarIndex}.png`);
      }
    };

    fetchAvatar();
  }, [user]);

  // Получаем статус Telegram
  useEffect(() => {
    const fetchTelegramStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('/api/auth/telegram/status', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setTelegramStatus(data);
        }
      } catch (error) {
        console.error('Error fetching Telegram status:', error);
      }
    };

    const fetchTelegramNotificationsStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('/api/auth/telegram/notifications-status', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setTelegramNotificationsStatus(data);
        }
      } catch (error) {
        console.error('Error fetching Telegram notifications status:', error);
      }
    };

    fetchTelegramStatus();
    fetchTelegramNotificationsStatus();
  }, []);

  // Обновляем статус когда открывается модалка
  useEffect(() => {
    if (isSettingsOpen) {
      const fetchTelegramStatus = async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch('/api/auth/telegram/status', {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            setTelegramStatus(data);
          }
        } catch (error) {
          console.error('Error fetching Telegram status:', error);
        }
      };

      const fetchTelegramNotificationsStatus = async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch('/api/auth/telegram/notifications-status', {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            setTelegramNotificationsStatus(data);
          }
        } catch (error) {
          console.error('Error fetching Telegram notifications status:', error);
        }
      };

      const fetchPinStatus = async () => {
        try {
          const token = localStorage.getItem('token');
          const res = await fetch('/api/auth/pin/status', { headers: { Authorization: `Bearer ${token}` } });
          if (res.ok) {
            const data = await res.json();
            setHasPin(!!data.hasPin);
          }
        } catch (err) {
          console.error('Error fetching PIN status:', err);
        }
      };

      fetchTelegramStatus();
      fetchTelegramNotificationsStatus();
      fetchPinStatus();
    }
  }, [isSettingsOpen]);

  useEffect(() => {
    if (!isSettingsOpen) {
      setEditingBirth(false);
    }
  }, [isSettingsOpen]);

  useEffect(() => {
    if (!isSettingsOpen) return;
    setBirthDraft(user?.birthDate || '');
    setBirthInputText(user?.birthDate ? isoToRuDisplay(user.birthDate) : '');
    setBirthError('');
    const sync = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const response = await fetch('/api/auth/verify', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.user) {
            if (onUserUpdated) onUserUpdated(data.user);
            setBirthDraft(data.user.birthDate || '');
            setBirthInputText(data.user.birthDate ? isoToRuDisplay(data.user.birthDate) : '');
          }
        }
      } catch (e) {
        console.error('verify sync failed:', e);
      }
    };
    sync();
  }, [isSettingsOpen, onUserUpdated]);

  useEffect(() => {
    if (isSettingsOpen) {
      setBirthDraft(user?.birthDate || '');
      if (!editingBirth) {
        setBirthInputText(user?.birthDate ? isoToRuDisplay(user.birthDate) : '');
      }
    }
  }, [user?.birthDate, isSettingsOpen, editingBirth]);

  const startEditBirth = () => {
    setBirthError('');
    let iso = user?.birthDate || birthDraft;
    if (!iso) {
      const t = new Date();
      t.setFullYear(t.getFullYear() - 25);
      t.setHours(0, 0, 0, 0);
      iso = formatDateForServer(t);
    }
    setBirthDraft(iso);
    setBirthInputText(isoToRuDisplay(iso));
    setEditingBirth(true);
  };

  const cancelEditBirth = () => {
    setEditingBirth(false);
    setBirthDraft(user?.birthDate || '');
    setBirthInputText(user?.birthDate ? isoToRuDisplay(user.birthDate) : '');
    setBirthError('');
  };

  const applyManualBirthInput = () => {
    const trimmed = birthInputText.trim();
    if (!trimmed) {
      setBirthError('');
      return;
    }
    const p = parseBirthDateInput(trimmed);
    if (!p.ok) {
      setBirthError(p.error);
      return;
    }
    setBirthError('');
    setBirthDraft(p.iso);
    setBirthInputText(isoToRuDisplay(p.iso));
  };

  const handleSaveBirthDate = async () => {
    const p = parseBirthDateInput(birthInputText.trim() || birthDraft);
    if (!p.ok) {
      setBirthError(p.error);
      return;
    }
    const iso = p.iso;
    setBirthSaving(true);
    setBirthError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/auth/birth-date', {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ birthDate: iso })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setBirthError(data.error || 'Не удалось сохранить');
        return;
      }
      if (data.user && onUserUpdated) {
        onUserUpdated(data.user);
      }
      setEditingBirth(false);
    } catch (err) {
      setBirthError('Ошибка сети');
    } finally {
      setBirthSaving(false);
    }
  };

  const handleLinkTelegram = async () => {
    setTelegramLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/auth/telegram/generate-link', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTelegramLinkUrl(data.linkUrl);
        window.open(data.linkUrl, '_blank');
      }
    } catch (error) {
      console.error('Error generating Telegram link:', error);
    } finally {
      setTelegramLoading(false);
    }
  };

  const handleSetPin = async (e) => {
    e.preventDefault();
    setPinError('');
    setPinSuccess('');
    const pin = setPinForm.pin.replace(/\D/g, '');
    if (pin.length < 4 || pin.length > 8) {
      setPinError('PIN должен быть от 4 до 8 цифр');
      return;
    }
    if (pin !== setPinForm.confirm.replace(/\D/g, '')) {
      setPinError('PIN-коды не совпадают');
      return;
    }
    setPinLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/auth/pin/set', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: setPinForm.password, pin })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setPinError(data.error || 'Ошибка при установке PIN');
        return;
      }
      setPinSuccess('PIN-код установлен. С этого устройства вход по PIN не будет требовать подтверждения в Telegram.');
      setHasPin(true);
      setShowSetPin(false);
      setSetPinForm({ password: '', pin: '', confirm: '' });
    } catch (err) {
      setPinError('Ошибка при установке PIN');
    } finally {
      setPinLoading(false);
    }
  };

  const handleRemovePin = async (e) => {
    e.preventDefault();
    setPinError('');
    setPinSuccess('');
    setPinLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/auth/pin/remove', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: removePinPassword })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setPinError(data.error || 'Ошибка при удалении PIN');
        return;
      }
      setPinSuccess('PIN-код удалён');
      setHasPin(false);
      setShowRemovePin(false);
      setRemovePinPassword('');
    } catch (err) {
      setPinError('Ошибка при удалении PIN');
    } finally {
      setPinLoading(false);
    }
  };

  const handleUnlinkTelegram = async () => {
    if (!window.confirm('Вы уверены, что хотите отвязать Telegram? После этого вам потребуется привязать его заново для входа.')) {
      return;
    }

    setTelegramLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/auth/telegram/unlink', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setTelegramStatus({ linked: false, username: null });
        setTelegramLinkUrl('');
      }
    } catch (error) {
      console.error('Error unlinking Telegram:', error);
    } finally {
      setTelegramLoading(false);
    }
  };

  const username = user?.discordUsername || user?.email?.split('@')[0] || 'Пользователь';
  const roles = roleCheckStatus?.includes('✅') 
    ? roleCheckStatus.replace('✅ ', '').split(', ')
    : [];

  // Определяем иерархию ролей (от самой высокой к низкой)
  const roleHierarchy = [
    'admin',
    'Chief Manager',
    'Deputy Chief Manager',


    'Senior Manager',
    'Curator Media'
  ];

  // Получаем самую высокую роль
  const getHighestRole = (roles) => {
    if (!roles || roles.length === 0) return null;
    
    for (const role of roleHierarchy) {
      if (roles.includes(role)) {
        return role;
      }
    }
    
    // Если роль не найдена в иерархии, возвращаем первую
    return roles[0];
  };

  const highestRole = getHighestRole(roles);

  const handleThemeChange = (newTheme) => {
    changeTheme(newTheme);
  };

  return (
    <>
      <div className="user-profile user-profile-button" onClick={() => setIsSettingsOpen(true)}>
        <div className="user-avatar">
          {avatarUrl ? (
            <img src={avatarUrl} alt={username} />
          ) : (
            <div className="user-avatar-placeholder">
              {username.charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        <div className="user-info">
          <div className="user-name">{username}</div>
          <div className="user-roles">
            {highestRole ? (
              <span className="user-role">{highestRole}</span>
            ) : (
              <span className="user-role">Модератор</span>
            )}
          </div>
        </div>
      </div>

      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => {
          setIsSettingsOpen(false);
          if (onClose) {
            onClose();
          }
        }}
        title="Настройки профиля"
        isMobileView={isMobileView}
      >
              {/* Информация о профиле */}
              <div className="profile-info-section">
                <div className="profile-header">
                  <div className="profile-avatar-large">
                    {avatarUrl ? (
                      <img src={avatarUrl} alt={username} />
                    ) : (
                      <div className="profile-avatar-placeholder-large">
                        {username.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="profile-header-info">
                    <h3 className="profile-name">{username}</h3>
                    {highestRole && (
                      <div className="profile-role-badge">{highestRole}</div>
                    )}
                  </div>
                </div>

                <div className="profile-info-grid">
                  <div className="profile-info-item">
                    <div className="profile-info-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                        <polyline points="22,6 12,13 2,6"></polyline>
                      </svg>
                    </div>
                    <div className="profile-info-content">
                      <div className="profile-info-label">Email</div>
                      <div className="profile-info-value">{user?.email || 'Не указан'}</div>
                    </div>
                  </div>

                  <div className="profile-info-item">
                    <div className="profile-info-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                    </div>
                    <div className="profile-info-content">
                      <div className="profile-info-label">Discord</div>
                      <div className="profile-info-value">{user?.discordUsername || 'Не указан'}</div>
                    </div>
                  </div>

                  <div
                    className={
                      'profile-info-item profile-info-item-birth' +
                      (!editingBirth ? ' profile-info-item-with-action' : '')
                    }
                  >
                    <div className="profile-info-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    </div>
                    <div className="profile-info-content">
                      <div className="profile-info-label">День рождения</div>
                      {!editingBirth ? (
                        user?.birthDate ? (
                          <div className="profile-info-value">{formatBirthRu(user.birthDate)}</div>
                        ) : (
                          <div className="profile-info-value muted">Не указан</div>
                        )
                      ) : (
                        <>
                          <div className="profile-birth-manual">
                            <label className="profile-birth-manual-label" htmlFor="profile-birth-input">
                              Введите дату
                            </label>
                            <input
                              id="profile-birth-input"
                              type="text"
                              className="profile-birth-manual-input"
                              placeholder="ДД.ММ.ГГГГ"
                              value={birthInputText}
                              onChange={(e) => {
                                setBirthInputText(e.target.value);
                                setBirthError('');
                              }}
                              onBlur={applyManualBirthInput}
                              disabled={birthSaving}
                              autoComplete="bday"
                            />
                          </div>
                          <div className="profile-birth-actions">
                            <button
                              type="button"
                              className="profile-birth-save"
                              onClick={handleSaveBirthDate}
                              disabled={birthSaving}
                            >
                              {birthSaving ? 'Сохранение...' : 'Сохранить'}
                            </button>
                            <button
                              type="button"
                              className="profile-birth-cancel"
                              onClick={cancelEditBirth}
                              disabled={birthSaving}
                            >
                              Отмена
                            </button>
                          </div>
                          {birthError && <div className="profile-birth-error">{birthError}</div>}
                        </>
                      )}
                    </div>
                    {!editingBirth && (
                      <button
                        type="button"
                        className="profile-info-edit-btn"
                        onClick={startEditBirth}
                        disabled={birthSaving}
                        title={user?.birthDate ? 'Изменить дату рождения' : 'Указать дату рождения'}
                        aria-label={user?.birthDate ? 'Изменить дату рождения' : 'Указать дату рождения'}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                    )}
                  </div>

                  <div className="profile-info-item">
                    <div className="profile-info-icon">
                      <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" className="telegram-icon">
                        <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.912.489-1.302.481-.428-.008-1.252-.241-1.865-.44-.752-.244-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635.099-.002.321.023.465.141.121.099.154.232.17.325.015.094.034.31.019.478z"/>
                      </svg>
                    </div>
                    <div className="profile-info-content">
                      <div className="profile-info-label">УВЕДОМЛЕНИЯ TELEGRAM</div>
                      <div className="profile-info-value">
                        {telegramNotificationsStatus.linked ? (
                          <span className="profile-info-status linked">
                            ✓ Привязан {telegramNotificationsStatus.username && `(@${telegramNotificationsStatus.username})`}
                          </span>
                        ) : (
                          <span className="profile-info-status not-linked">Не привязан</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="profile-info-item profile-info-item-with-action">
                    <div className="profile-info-icon">
                      <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" className="telegram-icon">
                        <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.912.489-1.302.481-.428-.008-1.252-.241-1.865-.44-.752-.244-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635.099-.002.321.023.465.141.121.099.154.232.17.325.015.094.034.31.019.478z"/>
                      </svg>
                    </div>
                    <div className="profile-info-content">
                      <div className="profile-info-label">ДВУХФАКТОРНАЯ АВТОРИЗАЦИЯ TELEGRAM</div>
                      <div className="profile-info-value">
                        {telegramStatus.linked ? (
                          <span className="profile-info-status linked">
                            ✓ Привязан {telegramStatus.username && `(@${telegramStatus.username})`}
                          </span>
                        ) : (
                          <span className="profile-info-status not-linked">Не привязан</span>
                        )}
                      </div>
                    </div>
                    {telegramStatus.linked && (
                      <button 
                        className="profile-info-unlink-btn"
                        onClick={handleUnlinkTelegram}
                        disabled={telegramLoading}
                        title="Отвязать Telegram"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="settings-section">
                <h3>Быстрый вход по PIN</h3>
                <div className="profile-pin-panel">
                  <div className="profile-pin-panel-header">
                    <div className="profile-pin-panel-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                    </div>
                    <div className="profile-pin-panel-title">
                      <span className="profile-pin-panel-label">PIN-код</span>
                      <span className="profile-pin-panel-value">
                        {hasPin ? 'Установлен' : 'Не установлен'}
                      </span>
                    </div>
                  </div>
                  <div className="profile-pin-panel-body">
                    <p className="profile-pin-desc">Вход по PIN с этого устройства без подтверждения в Telegram. С нового IP потребуется PIN и подтверждение в Telegram.</p>
                    {pinError && <div className="profile-pin-error">{pinError}</div>}
                    {pinSuccess && <div className="profile-pin-success">{pinSuccess}</div>}
                    {!hasPin ? (
                      <>
                        {!showSetPin ? (
                          <button type="button" className="profile-pin-btn profile-pin-btn-main" onClick={() => { setShowSetPin(true); setPinError(''); setPinSuccess(''); }}>
                            Установить PIN
                          </button>
                        ) : (
                          <form onSubmit={handleSetPin} className="profile-pin-form">
                            <div className="profile-pin-field">
                              <label>Текущий пароль</label>
                              <input
                                type="password"
                                placeholder="Введите пароль"
                                value={setPinForm.password}
                                onChange={(e) => setSetPinForm(prev => ({ ...prev, password: e.target.value }))}
                                required
                                className="profile-pin-input"
                              />
                            </div>
                            <div className="profile-pin-field">
                              <label>Новый PIN (4–8 цифр)</label>
                              <input
                                type="password"
                                placeholder="••••"
                                inputMode="numeric"
                                autoComplete="off"
                                value={setPinForm.pin}
                                onChange={(e) => setSetPinForm(prev => ({ ...prev, pin: e.target.value.replace(/\D/g, '').slice(0, 8) }))}
                                maxLength={8}
                                className="profile-pin-input"
                              />
                            </div>
                            <div className="profile-pin-field">
                              <label>Повторите PIN</label>
                              <input
                                type="password"
                                placeholder="••••"
                                inputMode="numeric"
                                autoComplete="off"
                                value={setPinForm.confirm}
                                onChange={(e) => setSetPinForm(prev => ({ ...prev, confirm: e.target.value.replace(/\D/g, '').slice(0, 8) }))}
                                maxLength={8}
                                className="profile-pin-input"
                              />
                            </div>
                            <div className="profile-pin-form-actions">
                              <button type="submit" className="profile-pin-btn primary" disabled={pinLoading}>
                                {pinLoading ? 'Сохранение...' : 'Сохранить'}
                              </button>
                              <button type="button" className="profile-pin-btn secondary" onClick={() => { setShowSetPin(false); setSetPinForm({ password: '', pin: '', confirm: '' }); setPinError(''); }}>
                                Отмена
                              </button>
                            </div>
                          </form>
                        )}
                      </>
                    ) : (
                      <>
                        {!showRemovePin ? (
                          <button type="button" className="profile-pin-btn danger" onClick={() => { setShowRemovePin(true); setPinError(''); setPinSuccess(''); }}>
                            Удалить PIN
                          </button>
                        ) : (
                          <form onSubmit={handleRemovePin} className="profile-pin-form">
                            <div className="profile-pin-field">
                              <label>Пароль для подтверждения</label>
                              <input
                                type="password"
                                placeholder="Введите пароль"
                                value={removePinPassword}
                                onChange={(e) => setRemovePinPassword(e.target.value)}
                                required
                                className="profile-pin-input"
                              />
                            </div>
                            <div className="profile-pin-form-actions">
                              <button type="submit" className="profile-pin-btn danger" disabled={pinLoading}>
                                {pinLoading ? 'Удаление...' : 'Удалить PIN'}
                              </button>
                              <button type="button" className="profile-pin-btn secondary" onClick={() => { setShowRemovePin(false); setRemovePinPassword(''); setPinError(''); }}>
                                Отмена
                              </button>
                            </div>
                          </form>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="settings-section">
                <h3>Тема</h3>
                <div className="theme-selector">
                  <button
                    className={`theme-option ${theme === 'light' ? 'active' : ''}`}
                    onClick={() => handleThemeChange('light')}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="5"></circle>
                      <line x1="12" y1="1" x2="12" y2="3"></line>
                      <line x1="12" y1="21" x2="12" y2="23"></line>
                      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                      <line x1="1" y1="12" x2="3" y2="12"></line>
                      <line x1="21" y1="12" x2="23" y2="12"></line>
                      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
                    </svg>
                    <span>Светлая</span>
                  </button>
                  <button
                    className={`theme-option ${theme === 'dark' ? 'active' : ''}`}
                    onClick={() => handleThemeChange('dark')}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                    </svg>
                    <span>Тёмная</span>
                  </button>
                </div>
              </div>


              <div className="settings-section">
                <button className="logout-button" onClick={onLogout}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                    <polyline points="16 17 21 12 16 7"></polyline>
                    <line x1="21" y1="12" x2="9" y2="12"></line>
                  </svg>
                  <span>Выйти</span>
                </button>
              </div>
      </SettingsModal>
    </>
  );
};

export default UserProfile;
